import { useState, useEffect, useRef, type ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { BackupService } from '../services/backup-service';
import type { BackupFile, BackupStats, BackupStatus, BackupLog, RcloneRemote } from '../types/backup';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { parseDbTimestamp } from '../utils/datetime';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Clock,
  UploadCloud,
  Container,
  Database,
  FileSpreadsheet,
  Folder,
  HardDrive,
  HeartPulse,
  type LucideIcon,
  Package,
  PauseCircle,
  RefreshCw,
  ScrollText,
  Server,
  Settings2,
  ShieldCheck
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { NativeSelect } from "@/components/ui/native-select"
import { DataTable } from "@/components/ui/data-table"
import { cn } from '@/utils/cn';

type BackupTab = 'backups' | 'logs' | 'config';

const backupTypeIcons: Record<string, LucideIcon> = {
  database: Database,
  files: Folder,
  csv: FileSpreadsheet,
  volume: Container
};

function getBackupTypeName(type: string) {
  switch (type) {
    case 'database': return 'Banco de Dados';
    case 'files': return 'Arquivos';
    case 'csv': return 'CSV/Planilhas';
    case 'volume': return 'Volume Docker';
    case 'full': return 'Completo';
    default: return type;
  }
}

const categoryBadgeVariants: Record<BackupFile['category'], 'default' | 'secondary' | 'outline'> = {
  daily: 'secondary',
  weekly: 'outline',
  monthly: 'default',
  csv: 'outline'
};

export default function BackupPage() {
  const [stats, setStats] = useState<BackupStats | null>(null);
  const [status, setStatus] = useState<BackupStatus | null>(null);
  const [backups, setBackups] = useState<BackupFile[]>([]);
  const [logs, setLogs] = useState<BackupLog[]>([]);
  const [remotes, setRemotes] = useState<RcloneRemote[]>([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [activeTab, setActiveTab] = useState<BackupTab>('backups');
  const [showGoogleDriveModal, setShowGoogleDriveModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<'daily' | 'weekly' | 'monthly' | 'csv' | 'all'>('all');
  const backupsRequestId = useRef(0);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'all') {
      loadBackups();
    } else {
      loadBackups(selectedCategory);
    }
  }, [selectedCategory]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsData, statusData, backupsData, logsData, remotesData] = await Promise.all([
        BackupService.getStats(),
        BackupService.getStatus(),
        BackupService.listBackups(),
        BackupService.getLogs(50),
        BackupService.listRcloneRemotes().catch(() => [])
      ]);

      setStats(statsData);
      setStatus(statusData);
      setBackups(backupsData);
      setLogs(logsData);
      setRemotes(remotesData);
    } catch (error: unknown) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados de backup');
    } finally {
      setLoading(false);
    }
  };

  const loadBackups = async (category?: 'daily' | 'weekly' | 'monthly' | 'csv') => {
    const requestId = ++backupsRequestId.current;
    try {
      const data = await BackupService.listBackups(category);
      if (requestId !== backupsRequestId.current) return;
      setBackups(data);
    } catch (error) {
      console.error('Erro ao carregar backups:', error);
    }
  };

  const handleExecuteBackup = async (type: 'database' | 'files' | 'full' | 'csv') => {
    const typeNames = {
      database: 'banco de dados',
      files: 'arquivos',
      full: 'completo',
      csv: 'CSV (planilhas)'
    };

    if (!confirm(`Deseja executar backup ${typeNames[type]} agora?`)) {
      return;
    }

    setExecuting(true);
    const loadingToast = toast.loading(`Executando backup ${typeNames[type]}...`);

    try {
      const result = await BackupService.executeBackup(type);

      if (result.success) {
        toast.success(`Backup ${typeNames[type]} concluído com sucesso!`, {
          id: loadingToast
        });
        await loadData(); // Recarregar dados
      } else {
        toast.error(`Erro ao executar backup: ${result.message}`, {
          id: loadingToast
        });
      }
    } catch (error: unknown) {
      console.error('Erro ao executar backup:', error);
      toast.error(`Erro ao executar backup ${typeNames[type]}`, {
        id: loadingToast
      });
    } finally {
      setExecuting(false);
    }
  };

  const handleUploadToCloud = async () => {
    if (!confirm('Deseja fazer upload dos backups para a nuvem agora? Isto pode levar alguns minutos.')) {
      return;
    }

    setExecuting(true);
    const loadingToast = toast.loading('Fazendo upload para nuvem...');

    try {
      const result = await BackupService.uploadToCloud();

      if (result.success) {
        toast.success('Upload concluído com sucesso!', {
          id: loadingToast
        });
      } else {
        toast.error(`Erro ao fazer upload: ${result.message}`, {
          id: loadingToast
        });
      }
    } catch (error: unknown) {
      console.error('Erro ao fazer upload:', error);
      toast.error('Erro ao fazer upload para nuvem', {
        id: loadingToast
      });
    } finally {
      setExecuting(false);
    }
  };

  const handleCheckHealth = async () => {
    const loadingToast = toast.loading('Verificando saúde do sistema...');

    try {
      const result = await BackupService.checkHealth();

      if (result.success) {
        toast.success('Sistema de backup está saudável!', {
          id: loadingToast
        });
      } else {
        toast.warning(`Problemas encontrados: ${result.message}`, {
          id: loadingToast,
          duration: 5000
        });
      }
    } catch (error: unknown) {
      console.error('Erro ao verificar saúde:', error);
      toast.error('Erro ao verificar saúde do sistema', {
        id: loadingToast
      });
    }
  };

  const formatDate = (date: Date | string | null | undefined) => {
    const dateObj = parseDbTimestamp(date);
    if (!dateObj) return 'N/A';
    return formatDistanceToNow(dateObj, { addSuffix: true, locale: ptBR });
  };


  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <span className="sr-only">Carregando backups...</span>
        <Skeleton className="h-8 w-56" />
        <KpiGrid>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
        </KpiGrid>
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const backupColumns: ColumnDef<BackupFile>[] = [
    {
      accessorKey: 'type',
      header: 'Tipo',
      cell: ({ row }) => <BackupTypeIcon type={row.original.type} />,
    },
    {
      accessorKey: 'name',
      header: 'Nome do Arquivo',
      cell: ({ row }) => (
        <span className="break-all font-mono text-sm">{row.original.name}</span>
      ),
    },
    {
      accessorKey: 'date',
      header: 'Data',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{formatDate(row.original.date)}</span>
      ),
    },
    {
      accessorKey: 'sizeFormatted',
      header: 'Tamanho',
      cell: ({ row }) => (
        <span className="text-sm tabular-nums text-muted-foreground">{row.original.sizeFormatted}</span>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Categoria',
      cell: ({ row }) => (
        <Badge variant={categoryBadgeVariants[row.original.category] ?? 'outline'}>{row.original.category}</Badge>
      ),
    },
  ];

  const quickActions: Array<{ label: string; icon: LucideIcon; onClick: () => void; disabled: boolean }> = [
    { label: 'Backup DB', icon: Database, onClick: () => handleExecuteBackup('database'), disabled: executing },
    { label: 'Backup Arquivos', icon: Folder, onClick: () => handleExecuteBackup('files'), disabled: executing },
    { label: 'Backup Completo', icon: Package, onClick: () => handleExecuteBackup('full'), disabled: executing },
    { label: 'Exportar CSV', icon: FileSpreadsheet, onClick: () => handleExecuteBackup('csv'), disabled: executing },
    { label: 'Upload Nuvem', icon: UploadCloud, onClick: handleUploadToCloud, disabled: executing || !status?.rcloneConfigured },
    { label: 'Verificar Saúde', icon: HeartPulse, onClick: handleCheckHealth, disabled: executing },
  ];

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-6 shrink-0 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Sistema de Backup</h1>
          </div>
          <p className="text-sm text-muted-foreground">Gerencie backups automáticos e manuais do sistema</p>
        </div>
      </div>

      <KpiGrid>
        <StatCard
          label="Último Backup"
          icon={Clock}
          value={formatDate(stats?.lastBackup)}
          detail={stats?.lastBackupType ? getBackupTypeName(stats.lastBackupType) : undefined}
        />
        <StatCard
          label="Total de Backups"
          icon={Package}
          value={stats?.totalBackups || 0}
          detail={stats?.totalSizeFormatted || '0 B'}
        />
        <StatCard
          label="Espaço Usado"
          icon={HardDrive}
          value={stats?.diskSpaceUsed || 'N/A'}
          detail={`Disponível: ${stats?.diskSpaceAvailable || 'N/A'}`}
        />
        <StatCard label="Status do Sistema" icon={Server}>
          <div className="flex flex-col gap-1">
            <StatusBadge label="Docker" status={status?.dockerRunning || false} />
            <StatusBadge label="Cron" status={status?.cronConfigured || false} />
            <StatusBadge label="Nuvem" status={status?.rcloneConfigured || false} />
          </div>
        </StatCard>
      </KpiGrid>

      <Card>
        <CardHeader>
          <CardTitle><h2>Ações Rápidas</h2></CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {quickActions.map(({ label, icon: Icon, onClick, disabled }) => (
            <Button
              key={label}
              variant="outline"
              onClick={onClick}
              disabled={disabled}
              className="h-auto min-w-0 flex-col gap-2 whitespace-normal py-4"
            >
              <Icon className="size-6 text-muted-foreground" aria-hidden="true" />
              <span className="text-sm font-medium">{label}</span>
            </Button>
          ))}
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as BackupTab)} className="min-w-0 gap-4">
        <div className="min-w-0 overflow-x-auto border-b border-border">
          <TabsList variant="line" className="h-auto min-w-max justify-start">
            <TabsTrigger value="backups" className="gap-2 py-2"><Package className="size-4" /> Backups</TabsTrigger>
            <TabsTrigger value="logs" className="gap-2 py-2"><ScrollText className="size-4" /> Logs</TabsTrigger>
            <TabsTrigger value="config" className="gap-2 py-2"><Settings2 className="size-4" /> Configuração</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="backups" className="flex min-w-0 flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Label htmlFor="backup-filter-category">Filtrar:</Label>
            <NativeSelect
              id="backup-filter-category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as never)}
            >
              <option value="all">Todos</option>
              <option value="daily">Diários</option>
              <option value="weekly">Semanais</option>
              <option value="monthly">Mensais</option>
              <option value="csv">CSV/Planilhas</option>
            </NativeSelect>
          </div>

          <Card className="min-w-0 py-0">
            <div className="min-w-0 p-2">
              <DataTable
                columns={backupColumns}
                data={backups}
                minWidth="min-w-[720px]"
                emptyMessage="Nenhum backup encontrado"
                pageSize={20}
              />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="flex min-w-0 flex-col gap-4">
          <div className="max-h-96 overflow-y-auto rounded-lg border border-border bg-muted p-4 font-mono text-sm">
            {logs.length === 0 ? (
              <p className="text-muted-foreground">Nenhum log disponível</p>
            ) : (
              logs.map((log, index) => (
                <div
                  key={index}
                  className={cn(
                    'break-words py-1',
                    log.level === 'error' && 'text-destructive',
                    log.level === 'warning' && 'font-medium text-foreground',
                    log.level === 'info' && 'text-foreground'
                  )}
                >
                  <span className="text-muted-foreground">
                    [{parseDbTimestamp(log.timestamp)?.toLocaleString() ?? '-'}]
                  </span>{' '}
                  {log.message}
                </div>
              ))
            )}
          </div>
          <div className="flex justify-center">
            <Button variant="ghost" size="sm" onClick={loadData}>
              <RefreshCw className="size-4" /> Atualizar Logs
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="config" className="flex min-w-0 flex-col gap-4">
          <ConfigSection title="Status do Sistema">
            <ConfigItem
              label="Docker PostgreSQL"
              value={status?.dockerRunning ? 'Rodando' : 'Parado'}
              status={status?.dockerRunning || false}
            />
            <ConfigItem
              label="Cron Configurado"
              value={status?.cronConfigured ? 'Sim' : 'Não'}
              status={status?.cronConfigured || false}
            />
            <ConfigItem
              label="rclone Instalado"
              value={status?.rcloneInstalled ? 'Sim' : 'Não'}
              status={status?.rcloneInstalled || false}
            />
            <ConfigItem
              label="Google Drive Configurado"
              value={status?.rcloneConfigured ? 'Sim' : 'Não'}
              status={status?.rcloneConfigured || false}
            />
          </ConfigSection>

          <ConfigSection title="Google Drive">
            {status?.rcloneConfigured ? (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
                <p className="flex items-center gap-2 font-medium">
                  <CheckCircle2 className="size-4 shrink-0 text-primary" aria-hidden="true" />
                  Google Drive está configurado!
                </p>
                {remotes.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-muted-foreground">Remotes configurados:</p>
                    <ul className="mt-1 list-inside list-disc space-y-1 text-sm text-muted-foreground">
                      {remotes.map((remote, index) => (
                        <li key={index}>{remote.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-muted/50 p-4">
                <p className="flex items-center gap-2 font-medium">
                  <AlertTriangle className="size-4 shrink-0 text-destructive" aria-hidden="true" />
                  Google Drive não está configurado
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Configure o Google Drive para fazer backup automático na nuvem.
                </p>
                <Button className="mt-3" onClick={() => setShowGoogleDriveModal(true)}>
                  <BookOpen className="size-4" /> Ver Instruções de Configuração
                </Button>
              </div>
            )}
          </ConfigSection>

          <ConfigSection title="Agendamentos Automáticos">
            <ScheduleItem
              title="Backup Diário - Banco de Dados"
              schedule="Todo dia às 2h da manhã"
              active={status?.cronConfigured || false}
            />
            <ScheduleItem
              title="Backup Diário - Arquivos"
              schedule="Todo dia às 2h15 da manhã"
              active={status?.cronConfigured || false}
            />
            <ScheduleItem
              title="Backup Semanal Completo"
              schedule="Todo domingo às 3h da manhã"
              active={status?.cronConfigured || false}
            />
            <ScheduleItem
              title="Exportação CSV (Planilhas)"
              schedule="Todo sábado às 22h"
              active={status?.cronConfigured || false}
            />
            <ScheduleItem
              title="Upload para Nuvem"
              schedule="Todo dia 1 do mês às 4h"
              active={status?.rcloneConfigured || false}
            />
          </ConfigSection>

          <ConfigSection title="Comandos Úteis">
            <CommandLine command="crontab -l" description="Ver agendamentos configurados" />
            <CommandLine command="crontab -e" description="Editar agendamentos" />
            <CommandLine command="~/LawTech-Backups/scripts/check-health.sh" description="Verificar saúde do sistema" />
            <CommandLine command="tail -f ~/LawTech-Backups/backup.log" description="Ver logs em tempo real" />
          </ConfigSection>
        </TabsContent>
      </Tabs>

      <GoogleDriveModal open={showGoogleDriveModal} onClose={() => setShowGoogleDriveModal(false)} />
    </div>
  );
}

// Componentes Auxiliares
function BackupTypeIcon({ type }: { type: string }) {
  const Icon = backupTypeIcons[type] ?? Package;
  const name = getBackupTypeName(type);
  return (
    <span className="inline-flex items-center gap-2 text-sm" title={name}>
      <Icon className="size-5 text-muted-foreground" aria-hidden="true" />
      <span className="sr-only">{name}</span>
    </span>
  );
}

interface StatCardProps {
  label: string;
  icon: LucideIcon;
  value?: ReactNode;
  detail?: string;
  children?: ReactNode;
}

function StatCard({ label, icon: Icon, value, detail, children }: StatCardProps) {
  return (
    <Card size="sm">
      <CardContent className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <p className="text-sm text-muted-foreground">{label}</p>
          {value !== undefined && <p className="break-words text-2xl font-semibold">{value}</p>}
          {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
          {children}
        </div>
        <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
      </CardContent>
    </Card>
  );
}

function StatusBadge({ label, status }: { label: string; status: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span className={cn('size-2 rounded-full', status ? 'bg-primary' : 'bg-destructive')} aria-hidden="true" />
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

function ConfigSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle><h3>{title}</h3></CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">{children}</CardContent>
    </Card>
  );
}

function ConfigItem({ label, value, status }: { label: string; value: string; status: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md bg-muted px-3 py-2">
      <span className="min-w-0 text-sm font-medium">{label}</span>
      <span className={cn('shrink-0 text-sm font-semibold', status ? 'text-primary' : 'text-destructive')}>
        {value}
      </span>
    </div>
  );
}

function ScheduleItem({ title, schedule, active }: { title: string; schedule: string; active: boolean }) {
  const Icon = active ? CheckCircle2 : PauseCircle;
  return (
    <div className="flex items-start gap-3 rounded-md bg-muted p-3">
      <Icon
        className={cn('mt-0.5 size-4 shrink-0', active ? 'text-primary' : 'text-muted-foreground')}
        aria-label={active ? 'Ativo' : 'Pausado'}
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{schedule}</p>
      </div>
    </div>
  );
}

function CodeBlock({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <code className={cn('block break-all rounded-md border border-border bg-muted px-3 py-2 font-mono text-sm', className)}>
      {children}
    </code>
  );
}

function InlineCode({ children }: { children: ReactNode }) {
  return <code className="break-all rounded-sm bg-muted px-1.5 py-0.5 font-mono text-xs">{children}</code>;
}

function CommandLine({ command, description }: { command: string; description: string }) {
  return (
    <div>
      <p className="mb-1 text-xs text-muted-foreground">{description}:</p>
      <CodeBlock>{command}</CodeBlock>
    </div>
  );
}

function SetupStep({ number, title, children }: { number: number; title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="flex items-center gap-2 text-base font-semibold">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm text-primary-foreground">
          {number}
        </span>
        {title}
      </h3>
      <div className="flex flex-col gap-3 rounded-lg border border-border p-4">{children}</div>
    </section>
  );
}

function GoogleDriveModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent size="xl" fullScreenOnMobile className="max-h-[90vh] overflow-y-auto max-sm:max-h-dvh">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="size-5 text-muted-foreground" aria-hidden="true" />
            Configurar Google Drive
          </DialogTitle>
          <DialogDescription>
            Configure o Google Drive para fazer backup automático dos seus dados na nuvem. Tempo estimado: 10 minutos
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-w-0 flex-col gap-6">
          <SetupStep number={1} title="Instalar rclone">
            <p className="text-sm">Abra o Terminal e execute:</p>
            <CodeBlock>brew install rclone</CodeBlock>
            <p className="text-xs text-muted-foreground">Aguarde a instalação concluir (pode levar alguns minutos)</p>
          </SetupStep>

          <SetupStep number={2} title="Configurar Google Drive">
            <p className="text-sm">Execute o comando de configuração:</p>
            <CodeBlock>rclone config</CodeBlock>
            <div className="border-t border-border pt-3">
              <p className="mb-2 text-sm font-medium">Siga as instruções:</p>
              <ol className="space-y-2 text-sm">
                <li>1. Digite <InlineCode>n</InlineCode> para criar novo remote</li>
                <li>2. Nome: <InlineCode>gdrive</InlineCode></li>
                <li>3. Storage: Digite <InlineCode>drive</InlineCode> (Google Drive)</li>
                <li>4. Client ID: Deixe em branco (pressione Enter)</li>
                <li>5. Client Secret: Deixe em branco (pressione Enter)</li>
                <li>6. Scope: Digite <InlineCode>1</InlineCode> (Full access)</li>
                <li>7. Root folder ID: Deixe em branco (pressione Enter)</li>
                <li>8. Service Account: Deixe em branco (pressione Enter)</li>
                <li>9. Auto config: Digite <InlineCode>y</InlineCode></li>
                <li>10. Uma janela do navegador vai abrir - faça login com sua conta Google</li>
                <li>11. Autorize o acesso ao Google Drive</li>
                <li>12. Configure as a team drive: Digite <InlineCode>n</InlineCode></li>
                <li>13. Confirme: Digite <InlineCode>y</InlineCode></li>
                <li>14. Sair: Digite <InlineCode>q</InlineCode></li>
              </ol>
            </div>
          </SetupStep>

          <SetupStep number={3} title="Testar Conexão">
            <p className="text-sm">Verifique se a conexão está funcionando:</p>
            <CodeBlock>rclone lsd gdrive:</CodeBlock>
            <p className="text-xs text-muted-foreground">Deve mostrar as pastas do seu Google Drive</p>
          </SetupStep>

          <SetupStep number={4} title="Fazer Primeiro Upload">
            <p className="text-sm">Teste fazendo upload manual:</p>
            <CodeBlock>~/LawTech-Backups/scripts/upload-cloud.sh</CodeBlock>
            <p className="text-xs text-muted-foreground">
              Aguarde o upload concluir (pode levar alguns minutos dependendo do tamanho dos backups)
            </p>
          </SetupStep>

          <SetupStep number={5} title="Configurar Backup Automático">
            <p className="text-sm">Adicione ao cron para upload automático mensal:</p>
            <CodeBlock>crontab -e</CodeBlock>
            <p className="text-sm">Adicione esta linha (se ainda não estiver lá):</p>
            <CodeBlock className="whitespace-pre-wrap">
              0 4 1 * * $HOME/LawTech-Backups/scripts/upload-cloud.sh {'>>'}  $HOME/LawTech-Backups/cron.log 2{'>&'}1
            </CodeBlock>
            <p className="text-xs text-muted-foreground">Isto fará upload todo dia 1 do mês às 4h da manhã</p>
          </SetupStep>

          <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
            <p className="flex items-center gap-2 font-medium">
              <CheckCircle2 className="size-4 shrink-0 text-primary" aria-hidden="true" />
              Pronto!
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Agora seus backups serão enviados automaticamente para o Google Drive todo mês.
              Você também pode usar o botão &quot;Upload Nuvem&quot; nesta página para fazer upload manual a qualquer momento.
            </p>
          </div>

          <section className="flex flex-col gap-3">
            <h3 className="text-base font-semibold">Problemas Comuns</h3>
            <div className="border-l-4 border-border pl-4">
              <p className="font-medium">rclone: command not found</p>
              <p className="text-sm text-muted-foreground">
                Solução: Instale o Homebrew primeiro com: <InlineCode>/bin/bash -c &quot;$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)&quot;</InlineCode>
              </p>
            </div>
            <div className="border-l-4 border-border pl-4">
              <p className="font-medium">Erro de autenticação</p>
              <p className="text-sm text-muted-foreground">
                Solução: Delete a configuração (<InlineCode>rclone config delete gdrive</InlineCode>) e configure novamente
              </p>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button className="w-full" onClick={onClose}>Entendi, fechar instruções</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
